/**
 * features/finance/ui.tsx
 * ------------------------------------------------------------------
 * THE ACCOUNTING DESIGN LANGUAGE — a crisper, flatter look for the screens a
 * bursar lives in.
 *
 * Deliberately a DROP-IN for the app-wide primitives: the exports here carry
 * the same names and the same props as `SoftCard`, `StatChip`, `GradientHero`
 * and `glassFieldClass` in `components/ui/glass`. A finance screen adopts the
 * language by changing its import line — no JSX is touched, so a restyle across
 * fifty-odd screens cannot quietly break a layout or drop a prop.
 *
 * IT STARTED IN FINANCE AND IS NOW THE HOUSE STYLE. It began scoped to the
 * accounting screens because restyling the shared primitives in place would
 * have repainted every screen in the building to change the bursar's. That
 * worked, the school preferred it, and from September 2026 it is what every
 * screen moves to as it is touched, and what every new screen starts from.
 * Admissions was the first module outside finance to adopt it whole.
 *
 * THE PATH IS NOW A LIE AND STAYS ANYWAY. This is no longer a finance file,
 * but moving it would touch a hundred-odd import lines to say something this
 * paragraph says for free — and a rename landing in the middle of live work
 * is a hundred chances to break a screen for no behaviour change. If it
 * moves, it moves on a quiet day and on its own.
 *
 * ADOPTING IT IS AN IMPORT LINE. The exports carry the same names and props
 * as `components/ui/glass`, modal surfaces included, so a screen changes
 * where it imports from and touches no JSX. A restyle that cannot drop a
 * prop cannot quietly break a layout.
 *
 * `PageHeader` is exported as `GradientHero` as well, so a screen already using
 * the older gradient header adopts the language by changing an import line and
 * nothing else.
 *
 * The language itself:
 *   - flat rounded-xl panels with a hairline border, not soft rounded-2xl cards
 *   - a navy/gold gradient icon tile identifying each page
 *   - stat tiles that read left-to-right off a coloured accent bar, rather than
 *     centred gradient chips
 *   - compact, firmly-bordered form fields
 *
 * ON THE COLOURS. Every value below is a Tailwind token backed by
 * `lib/brand.palette` — `primary`, `secondary`, `primarySoft` and so on. Not a
 * hex literal, and this is not a style preference. An earlier draft of this kit
 * hardcoded the navy and gold, was ported to a sister school, and carried this
 * school's crest colours with it; that deployment's accounting screens wore the
 * wrong school's identity for months without anyone noticing, because hardcoded
 * hex looks exactly as correct as hex that was reasoned about. Tokens make the
 * kit portable: the same file dropped into another school's tree renders in
 * that school's colours, because its palette module says so.
 *
 * Purely visual. No logic, no data.
 * ------------------------------------------------------------------ */
import type { ReactNode } from 'react';
import { FileDown, Landmark, Sheet, type LucideIcon } from 'lucide-react';

/* ------------------------------- palette ------------------------------- */

/**
 * The school's navy and gold, as used by the page tiles and the PDF headers.
 * Both are brand tokens; see the note above on why not hex.
 */
const TILE = {
  navy: 'bg-gradient-to-br from-primary to-primaryLight',
  gold: 'bg-gradient-to-br from-secondary to-secondaryLight',
} as const;

/**
 * Accent per tone. Keyed by the tone names the finance screens ALREADY pass, so
 * the swap needs no edits at the call sites — including `warn`, which was never
 * a tone in the shared kit and had been silently falling back to slate.
 *
 * The brand tones carry a light and a dark variant because the crest navy
 * disappears on a dark panel and the crest gold disappears on a white one.
 */
const ACCENT: Record<string, { bar: string; text: string }> = {
  slate: { bar: 'border-l-slate-400', text: 'text-slate-700 dark:text-slate-200' },
  blue: { bar: 'border-l-primary', text: 'text-primary dark:text-primarySoft' },
  navy: { bar: 'border-l-primary', text: 'text-primary dark:text-primarySoft' },
  gold: { bar: 'border-l-secondary', text: 'text-secondaryDeep dark:text-secondary' },
  emerald: { bar: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  green: { bar: 'border-l-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
  red: { bar: 'border-l-red-500', text: 'text-red-600 dark:text-red-400' },
  warn: { bar: 'border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  amber: { bar: 'border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
  purple: { bar: 'border-l-purple-500', text: 'text-purple-600 dark:text-purple-300' },
  teal: { bar: 'border-l-teal-500', text: 'text-teal-600 dark:text-teal-300' },
};
const accentFor = (tone?: string) => ACCENT[tone ?? 'slate'] ?? ACCENT.slate;

/** The panel surface, shared by every container on an accounting page. */
const PANEL = 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/70 shadow-sm';

/* -------------------------------- panel -------------------------------- */

/**
 * Drop-in for `SoftCard`. Same props, flatter surface.
 *
 * `hover` is accepted and ignored: a lift-on-hover reads as "this is a button"
 * and almost none of these panels are clickable. The prop stays in the
 * signature so no call site has to change.
 */
export function SoftCard({
  children,
  className = '',
  hover: _hover = true,
  pad = 'p-5',
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  pad?: string;
}) {
  return <div className={`rounded-xl ${PANEL} ${pad} ${className}`}>{children}</div>;
}

/** A panel with a coloured left edge — for a total, a warning, a verdict. */
export function AccentPanel({
  children, className = '', tone = 'navy', pad = 'p-4',
}: { children: ReactNode; className?: string; tone?: string; pad?: string }) {
  return (
    <div className={`rounded-r-xl ${PANEL} border-l-4 ${accentFor(tone).bar} ${pad} ${className}`}>
      {children}
    </div>
  );
}

/* ------------------------------ stat tile ------------------------------ */

/**
 * Drop-in for `StatChip`. Same props — tone, value, label, optional icon.
 *
 * Reads left to right off an accent bar instead of centring a number in a
 * gradient. A row of figures a bursar scans down a column is easier to read
 * ranged left, and the accent carries the meaning the gradient used to.
 */
export function StatChip({
  tone = 'slate',
  value,
  label,
  icon: Icon,
}: {
  tone?: string;
  value: ReactNode;
  label: string;
  icon?: LucideIcon;
}) {
  const a = accentFor(tone);
  return (
    <div className={`rounded-r-xl ${PANEL} border-l-4 ${a.bar} px-4 py-3 flex items-center gap-3`}>
      {Icon && (
        <div className="w-9 h-9 rounded-lg bg-gray-50 dark:bg-gray-900/40 flex items-center justify-center shrink-0">
          <Icon size={18} className={a.text} />
        </div>
      )}
      <div className="min-w-0">
        <p className={`text-2xl font-bold leading-none tabular-nums truncate ${a.text}`}>{value}</p>
        <p className="text-[11px] text-gray-400 mt-1">{label}</p>
      </div>
    </div>
  );
}

/* ----------------------------- page header ----------------------------- */

/**
 * Drop-in for `GradientHero`, with an optional icon.
 *
 * The icon defaults rather than being required, so the import swap alone gives
 * a screen the language; a screen that names its own icon simply looks more
 * like itself.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  icon: Icon = Landmark,
  tone = 'navy',
}: {
  title: string;
  /** ReactNode, not string — the primitive this replaces accepts a node, and a
   *  narrower type here would refuse a screen whose subtitle carries markup. */
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: LucideIcon;
  tone?: keyof typeof TILE;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md shrink-0 ${TILE[tone]}`}>
          <Icon size={22} />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{title}</h1>
          {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * The name the existing finance screens import. Same component, so adopting the
 * accounting language is a one-line import change per screen.
 */
export const GradientHero = PageHeader;

/** A section heading inside a panel: the same gradient tile, smaller. */
export function PanelHeader({
  icon: Icon, title, subtitle, right, tone = 'navy',
}: { icon: LucideIcon; title: string; subtitle?: ReactNode; right?: ReactNode; tone?: keyof typeof TILE }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700/70">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0 ${TILE[tone]}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <h3 className="font-bold text-gray-900 dark:text-white leading-tight truncate">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-400 truncate">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

/* -------------------------------- fields -------------------------------- */

/**
 * Drop-in for `glassFieldClass`. A firmer, more compact field: a two-pixel
 * border that stays visible at rest, so a form of twenty inputs reads as a form
 * rather than a page of floating text. Keeps the focus ring — a keyboard user
 * needs to see where they are.
 */
export function glassFieldClass(extra = '') {
  return `w-full px-3 py-2 rounded-lg border-2 bg-white border-gray-200 text-gray-900 text-sm hover:border-gray-300 focus:border-primaryLight focus:ring-4 focus:ring-primary/10 focus:outline-none transition-colors dark:bg-gray-900 dark:border-gray-600 dark:text-white dark:hover:border-gray-500 ${extra}`;
}

/** The primary action button, in the school's navy. */
export const btnPrimary =
  'px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-primaryLight disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

/**
 * The export pair, deliberately NOT the primary navy.
 *
 * Two jobs: make them findable in the same corner of every screen without
 * hunting, and make them visibly not the button that changes something. A
 * download is safe and repeatable; the button that posts a journal is not, and
 * the two should never look alike. Crimson for PDF (the format's own colour),
 * green for the spreadsheet — both as tinted outlines rather than solid fills,
 * so a header with three buttons in it does not turn into a traffic light.
 */
const btnExport = 'px-3 py-2 rounded-lg border-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
export const btnPdf = `${btnExport} border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-300 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300 dark:hover:bg-rose-950/70`;
export const btnCsv = `${btnExport} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/70`;

/** The quiet action beside it. */
export const btnGhost =
  'px-4 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-600 text-sm font-semibold inline-flex items-center gap-1.5 hover:border-gray-300 dark:hover:border-gray-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

/* ------------------------------- exports ------------------------------- */

/**
 * The PDF / CSV pair, in the SAME PLACE on every accounting screen: the right
 * of the page header.
 *
 * A predictable location is the whole point. Export buried in a different
 * corner of each screen is why finance staff end up re-keying figures into
 * their own spreadsheet — they stop looking. Both buttons come off one
 * definition of the table (lib/tableExport), so the CSV and the PDF cannot
 * disagree about what the numbers are.
 *
 * `disabled` when there is nothing to export, rather than hidden: a button that
 * vanishes reads as a missing feature, one that is greyed reads as an empty
 * screen.
 */
/**
 * The kit's primary action button.
 *
 * Same shape as the old components/ui/glass AccentButton so a screen can be
 * moved across by changing one import line rather than every call site —
 * which is the difference between a page getting the accounting look and it
 * staying on the old kit because the migration looked expensive.
 */
export function AccentButton({
  icon: Icon,
  children,
  onClick,
  disabled,
  variant = 'accent',
  title,
  type = 'button',
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'accent' | 'soft';
  title?: string;
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={variant === 'accent' ? btnPrimary : btnGhost}
    >
      {Icon && <Icon size={15} />}
      {children}
    </button>
  );
}

export function ExportBar({
  onPdf, onCsv, disabled = false, label = 'Export',
}: { onPdf?: () => void; onCsv?: () => void; disabled?: boolean; label?: string }) {
  return (
    <div className="flex items-center gap-2" role="group" aria-label={label}>
      {onPdf && (
        <button type="button" onClick={onPdf} disabled={disabled} className={btnPdf} title="Download as PDF">
          <FileDown size={15} /> PDF
        </button>
      )}
      {onCsv && (
        <button type="button" onClick={onCsv} disabled={disabled} className={btnCsv} title="Download as a spreadsheet">
          <Sheet size={15} /> CSV
        </button>
      )}
    </div>
  );
}

/* -------------------------------- modals -------------------------------- */

/**
 * The modal surfaces, in this language rather than the glass one.
 *
 * Drop-in for `glassModalBackdrop` / `glassModalSurface`: same names with a
 * `kit` prefix, same shape, so a screen adopting the kit does not have to
 * keep one foot in the old file just for its dialogs. Flatter and firmer to
 * match the panels — a rounded-xl card with a hairline border and a plain
 * white ground, not a frosted rounded-2xl pane.
 */
export const modalBackdrop =
  'fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-[2px]';

export function modalSurface(extra = '') {
  return 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 '
    + `rounded-xl shadow-xl ${extra}`;
}
